import React, { useState, useEffect } from 'react';
import api from '../api';
import { Download, FileSpreadsheet, Shield } from 'lucide-react';

const AdminExport = () => {
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    user_id: '',
    format: 'xlsx',
  });
  const [auditFilters, setAuditFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/users');
        setUsers(res.data);
      } catch (error) {
        console.error('Fehler:', error);
      }
    };
    fetchUsers();
  }, []);

  const handleExportTimeEntries = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_id) params.append('user_id', filters.user_id);
      params.append('format', filters.format);

      const res = await api.get(`/export/time-entries?${params}`, { responseType: 'blob' });

      const ext = filters.format === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zeiterfassung_${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Fehler beim Export.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAuditLog = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (auditFilters.start_date) params.append('start_date', auditFilters.start_date);
      if (auditFilters.end_date) params.append('end_date', auditFilters.end_date);

      const res = await api.get(`/export/audit-log?${params}`, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_log_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Fehler beim Export.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Datenexport</h2>
        <p>Exportieren Sie Zeiterfassungsdaten und Audit-Logs</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span><FileSpreadsheet size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Zeiterfassung exportieren</span>
        </div>
        <div className="card-body">
          <div className="filters" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>Von</label>
              <input type="date" className="form-control" value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Bis</label>
              <input type="date" className="form-control" value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Mitarbeiter</label>
              <select className="form-control" value={filters.user_id}
                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}>
                <option value="">Alle Mitarbeiter</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Format</label>
              <select className="form-control" value={filters.format}
                onChange={(e) => setFilters({ ...filters, format: e.target.value })}>
                <option value="xlsx">Excel (XLSX)</option>
                <option value="csv">CSV</option>
              </select>
            </div>
          </div>
          <button className="btn btn-primary" onClick={handleExportTimeEntries} disabled={exporting}>
            <Download size={16} />
            {exporting ? 'Exportiert...' : 'Zeiterfassung exportieren'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span><Shield size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Audit-Log exportieren</span>
        </div>
        <div className="card-body">
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: 16 }}>
            Das Audit-Log enthält alle Änderungen an Zeiteinträgen und Abwesenheiten.
            Dies dient der Revisionssicherheit gemäß den deutschen gesetzlichen Anforderungen.
          </p>
          <div className="filters" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>Von</label>
              <input type="date" className="form-control" value={auditFilters.start_date}
                onChange={(e) => setAuditFilters({ ...auditFilters, start_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Bis</label>
              <input type="date" className="form-control" value={auditFilters.end_date}
                onChange={(e) => setAuditFilters({ ...auditFilters, end_date: e.target.value })} />
            </div>
          </div>
          <button className="btn btn-primary" onClick={handleExportAuditLog} disabled={exporting}>
            <Download size={16} />
            {exporting ? 'Exportiert...' : 'Audit-Log exportieren'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminExport;
